import React from 'react';
import { styled } from '@compiled/react';

import { N20 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { MediaImage } from '@atlaskit/media-image';
import type { MediaClientConfig } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import type { CoverPictureId } from '../pageCoverPictureTypes';

import { ImageWrapper } from './EditorPageCoverPicture';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingDiv = styled.div<{
	dynamicCoverImageHeight?: number;
}>({
	position: 'relative',
	height: '100%',
	background: token('color.background.accent.gray.subtlest', N20),
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => `${props.dynamicCoverImageHeight}px`,
	/* eslint-disable @atlaskit/design-system/ensure-design-token-usage/preview */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: (props) => `-${props.dynamicCoverImageHeight}px`,
	/* eslint-enable @atlaskit/design-system/ensure-design-token-usage/preview */
});

export type MediaCoverPictureProps = {
	showErrorFlag: () => void;
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	mediaConfig: {
		collection: string | undefined;
		authProvider: MediaClientConfig['authProvider'];
		onUploadRejection?: (data: UploadRejectionData) => boolean;
	};
	dynamicCoverImageHeight?: number;
};

export const MediaCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	showErrorFlag,
	mediaConfig,
}: MediaCoverPictureProps) => {
	return (
		<MediaImage
			identifier={{
				mediaItemType: 'file',
				id: String(coverPicture.current.draft?.value.id),
				collectionName: mediaConfig.collection,
			}}
			mediaClientConfig={{
				authProvider: mediaConfig.authProvider,
			}}
			//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
			key={String(coverPicture.current.draft?.value.id)}
			apiConfig={{
				allowAnimated: true,
				mode: 'full-fit' as const,
				upscale: false,
			}}
		>
			{({ data, loading, error }) => {
				if (loading) {
					return (
						<LoadingDiv
							data-testid="cover-pic-loading"
							dynamicCoverImageHeight={dynamicCoverImageHeight}
						/>
					);
				}
				if (!data) {
					return null;
				}
				if (error) {
					showErrorFlag();
				}
				return (
					<ImageWrapper
						data-testid="actual-cover-picture"
						id="actual-cover-picture"
						// need this empty alt tag for decorative images
						alt=""
						src={data.src}
						coverPicturePosition={coverPicture.current.draft?.value.position}
						dynamicCoverImageHeight={dynamicCoverImageHeight}
					/>
				);
			}}
		</MediaImage>
	);
};
